import { Button } from "@/components/ui/button";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Skeleton } from "@/components/ui/skeleton";
import { Switch } from "@/components/ui/switch";
import { formatCommissionAmount } from "@/lib/affiliateDashboard";

export type AffiliateConfig = {
  commission_bps: number;
  holdback_days: number;
  lapse_grace_days: number;
  min_payout_cents: number;
  paid_only_required: boolean;
};

type Props = {
  config: AffiliateConfig | null;
  setConfig: (c: AffiliateConfig) => void;
  loading: boolean;
  saving: boolean;
  isSuperAdmin: boolean;
  onSave: () => void;
};

export function AffiliateConfigCard({
  config,
  setConfig,
  loading,
  saving,
  isSuperAdmin,
  onSave,
}: Props) {
  return (
    <Card className="shadow-soft">
      <CardHeader>
        <CardTitle>Program configuration</CardTitle>
      </CardHeader>
      <CardContent className="space-y-4">
        {loading || !config ? (
          <Skeleton className="h-32 w-full" />
        ) : (
          <>
            <div className="grid gap-4 sm:grid-cols-2 lg:grid-cols-3">
              <div className="space-y-2">
                <Label htmlFor="bps">Commission rate (bps)</Label>
                <Input
                  id="bps"
                  type="number"
                  min={0}
                  max={10000}
                  value={config.commission_bps}
                  onChange={(e) =>
                    setConfig({ ...config, commission_bps: Number(e.target.value) })
                  }
                  disabled={!isSuperAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  {(config.commission_bps / 100).toFixed(2)}% of net invoice amount
                </p>
              </div>
              <div className="space-y-2">
                <Label htmlFor="hold">Holdback days</Label>
                <Input
                  id="hold"
                  type="number"
                  min={0}
                  max={365}
                  value={config.holdback_days}
                  onChange={(e) =>
                    setConfig({ ...config, holdback_days: Number(e.target.value) })
                  }
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="grace">Lapse grace days</Label>
                <Input
                  id="grace"
                  type="number"
                  min={0}
                  max={365}
                  value={config.lapse_grace_days}
                  onChange={(e) =>
                    setConfig({ ...config, lapse_grace_days: Number(e.target.value) })
                  }
                  disabled={!isSuperAdmin}
                />
              </div>
              <div className="space-y-2">
                <Label htmlFor="min">Min payout (cents)</Label>
                <Input
                  id="min"
                  type="number"
                  min={0}
                  value={config.min_payout_cents}
                  onChange={(e) =>
                    setConfig({ ...config, min_payout_cents: Number(e.target.value) })
                  }
                  disabled={!isSuperAdmin}
                />
                <p className="text-xs text-muted-foreground">
                  {formatCommissionAmount(config.min_payout_cents, "USD")}
                </p>
              </div>
              <div className="flex items-center justify-between rounded border border-border p-3 sm:col-span-2 lg:col-span-1">
                <div className="space-y-0.5">
                  <Label htmlFor="paid">Paid plan required</Label>
                  <p className="text-xs text-muted-foreground">
                    Only accrue commissions while affiliate is on a paid plan
                  </p>
                </div>
                <Switch
                  id="paid"
                  checked={config.paid_only_required}
                  onCheckedChange={(v) =>
                    setConfig({ ...config, paid_only_required: v })
                  }
                  disabled={!isSuperAdmin}
                />
              </div>
            </div>
            {isSuperAdmin ? (
              <div className="flex justify-end">
                <Button onClick={onSave} disabled={saving}>
                  {saving ? "Saving…" : "Save config"}
                </Button>
              </div>
            ) : (
              <p className="text-xs text-muted-foreground">
                Read-only — super-admin required to edit config.
              </p>
            )}
          </>
        )}
      </CardContent>
    </Card>
  );
}
